import { useState, useEffect } from "react";

import { css } from "@compiled/react";

import Avatar from "@atlaskit/avatar";
import Link from "@atlaskit/link";
import Skeleton from "@atlaskit/skeleton";
import { token } from "@atlaskit/tokens";

import { NotificationGroup as NotificationGroupType } from "../../types";
import { formatRelativeTime } from "../helpers/format-relative-time";
import { useToggleNotificationReadState } from "../hooks/use-toggle-notification-read-state";

import { ExpandGroupButton } from "./expand-group-button";
import { ReadStateIndicator } from "./read-state-indicator";

const GroupContainerStyle = css({
    marginBlockEnd: token("space.200")
});

const GroupTitleStyle = css({
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    fontSize: "12px",
    fontWeight: 600,
    color: token("color.text.subtlest"),
    marginBlockEnd: token("space.075"),
    marginBlockStart: 0,
    marginInlineStart: token("space.100")
});

const NotificationItemStyle = css({
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    marginBlockEnd: token("space.200"),
    borderRadius: token("border.radius.100")
});

const NotificationItemContentStyle = css({
    "cursor": "pointer",
    "display": "flex",
    "alignItems": "flex-start",
    "width": "100%",
    "padding": token("space.100"),
    "position": "relative",
    "&:hover": {
        backgroundColor: token("color.background.neutral.subtle.hovered")
    }
});

const bottomMarginStyle = css({
    marginBlockEnd: "16px"
});
const bottomMarginWithChildrenStyle = css({
    marginBlockEnd: "4px"
});

const AvatarContainerStyle = css({
    marginInlineEnd: token("space.100")
});

const linkWrapperStyle = css({
    display: "flex",
    alignItems: "flex-start"
});

const IconContainerStyle = css({
    display: "flex",
    paddingBlockStart: "4px",
    paddingInlineEnd: "4px"
});

const linkStyle = css({
    fontSize: "14px",
    lineHeight: "20px"
});

const firstNotificationStyle = css({
    margin: 0,
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    fontSize: "14px",
    fontWeight: 500
});

const timestampStyle = css({
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    fontSize: "14px",
    color: token("color.text.subtlest"),
    marginInlineStart: token("space.050")
});
const NestedSkeletonItemStyle = css({
    display: "flex",
    alignItems: "center",
    padding: token("space.100"),
    width: "100%",
    position: "relative"
});

const NestedAvatarSkeletonStyle = css({
    marginInlineEnd: token("space.100"),
    borderRadius: "50%"
});

const NestedTextSkeletonStyle = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    paddingInlineStart: token("space.100")
});

const nestedNotificationStyle = css({
    "cursor": "pointer",
    "position": "relative",
    "display": "flex",
    "alignItems": "center",
    "marginBlockEnd": token("space.200"),
    "padding": token("space.100"),
    "width": "100%",
    "&:hover": {
        backgroundColor: token("color.background.neutral.subtle.hovered")
    }
});

const UpperAvatarThreadStyle = css({
    position: "absolute",
    insetInlineStart: "29px",
    insetBlockStart: "-15px",
    width: "2px",
    height: "20px",
    backgroundColor: token("color.border")
});

const LowerAvatarThreadStyle = css({
    position: "absolute",
    insetInlineStart: "29px",
    insetBlockStart: "55px",
    width: "2px",
    height: "calc(100% - 54px)",
    backgroundColor: token("color.border")
});

interface NotificationGroupProps {
    title: string;
    notificationGroups: NotificationGroupType[];
    onExpand: (groupId: string, continuationToken?: string) => void;
    loadingGroupId?: string | null;
    notificationLogUrl: string;
}

const NotificationGroup = ({
    title,
    notificationGroups,
    onExpand,
    loadingGroupId,
    notificationLogUrl
}: NotificationGroupProps) => {
    const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [readStates, setReadStates] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};
        notificationGroups.forEach((group) => {
            group.notifications.forEach((notification) => {
                initial[notification.id] = notification.readState;
            });
        });
        return initial;
    });
    const { toggleReadState } = useToggleNotificationReadState();

    // Sync readStates with notificationGroups when notifications are expanded
    useEffect(() => {
        setReadStates((prev) => {
            const updated = { ...prev };
            notificationGroups.forEach((group) => {
                group.notifications.forEach((notification) => {
                    // Only update if the notification is not already in the state
                    // This preserves any toggled states
                    if (!(notification.id in updated)) {
                        updated[notification.id] = notification.readState;
                    }
                });
            });
            return updated;
        });
    }, [notificationGroups]);

    return (
        <div css={GroupContainerStyle}>
            <h5 css={GroupTitleStyle}>{title}</h5>
            {notificationGroups.map(
                // eslint-disable-next-line complexity
                (group) => {
                    const firstNotification = group.notifications[0];
                    const isExpanded = expandedGroupId === group.id;

                    if (!firstNotification) return null;

                    const isRead = readStates[firstNotification.id] === "read";

                    const hasMoreNotifications =
                        group.additionalActors &&
                        group.additionalActors.length > 0;

                    return (
                        <div key={group.id} css={NotificationItemStyle}>
                            {/* Main notification content */}
                            <div
                                id="notification-item-content"
                                role="button"
                                tabIndex={0}
                                css={[
                                    NotificationItemContentStyle,
                                    hasMoreNotifications
                                        ? bottomMarginWithChildrenStyle
                                        : bottomMarginStyle
                                ]}
                                onMouseEnter={() =>
                                    setHoveredId(firstNotification.id)
                                }
                                onMouseLeave={() => setHoveredId(null)}
                                onFocus={() =>
                                    setHoveredId(firstNotification.id)
                                }
                                onBlur={() => setHoveredId(null)}
                                onClick={() => {
                                    const url =
                                        firstNotification.content.entity?.link
                                            ?.url;
                                    if (url) {
                                        window.location.href = url;
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        const url =
                                            firstNotification.content.entity
                                                ?.link?.url;
                                        if (url) {
                                            window.location.href = url;
                                        }
                                    }
                                }}
                            >
                                <div css={AvatarContainerStyle}>
                                    <Avatar
                                        size="large"
                                        src={
                                            firstNotification.content
                                                .actors?.[0]?.avatarUrl
                                        }
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p css={firstNotificationStyle}>
                                        {firstNotification.content.message}
                                        <span css={timestampStyle}>
                                            {" "}
                                            {formatRelativeTime(
                                                firstNotification.timestamp
                                            )}
                                        </span>
                                    </p>
                                    <Link
                                        appearance="subtle"
                                        href={
                                            firstNotification.content.entity
                                                ?.link?.url
                                        }
                                    >
                                        <div css={linkWrapperStyle}>
                                            <div css={IconContainerStyle}>
                                                {/* eslint-disable-next-line no-restricted-syntax */}
                                                <img
                                                    src={
                                                        firstNotification
                                                            .content.entity
                                                            ?.icon?.url
                                                    }
                                                    height={12}
                                                    width={12}
                                                    alt={
                                                        firstNotification
                                                            .content.entity
                                                            ?.title
                                                    }
                                                />
                                            </div>

                                            <span css={linkStyle}>
                                                {
                                                    firstNotification.content
                                                        .entity?.title
                                                }
                                            </span>
                                        </div>
                                    </Link>
                                </div>
                                <ReadStateIndicator
                                    isRead={isRead}
                                    isCardHovered={
                                        firstNotification.id === hoveredId
                                    }
                                    onToggle={async (e) => {
                                        e.stopPropagation();
                                        const newState = isRead
                                            ? "unread"
                                            : "read";
                                        setReadStates((prev) => ({
                                            ...prev,
                                            [firstNotification.id]: newState
                                        }));
                                        await toggleReadState({
                                            notificationLogUrl,
                                            notificationId:
                                                firstNotification.id,
                                            isCurrentlyRead: isRead
                                        });
                                    }}
                                />
                                {isExpanded && (
                                    <div
                                        className="lower-avatar-thread"
                                        css={LowerAvatarThreadStyle}
                                    />
                                )}
                            </div>

                            {isExpanded && (
                                <>
                                    {loadingGroupId === group.id && (
                                        <>
                                            {[
                                                ...Array(
                                                    Math.min(group.size - 1, 5)
                                                )
                                            ].map((_, index) => (
                                                <div
                                                    key={`skeleton-${index}`}
                                                    css={
                                                        NestedSkeletonItemStyle
                                                    }
                                                >
                                                    {index === 0 && (
                                                        <div
                                                            className="upper-avatar-thread"
                                                            css={
                                                                UpperAvatarThreadStyle
                                                            }
                                                        />
                                                    )}
                                                    <div
                                                        css={
                                                            NestedAvatarSkeletonStyle
                                                        }
                                                    >
                                                        <Skeleton
                                                            width="44px"
                                                            height="44px"
                                                        />
                                                    </div>
                                                    <div
                                                        css={
                                                            NestedTextSkeletonStyle
                                                        }
                                                    >
                                                        <Skeleton
                                                            width="50%"
                                                            height="12px"
                                                        />
                                                        <Skeleton
                                                            width="70%"
                                                            height="12px"
                                                        />
                                                    </div>
                                                    {index <
                                                        Math.min(
                                                            group.size - 1,
                                                            5
                                                        ) -
                                                            1 && (
                                                        <div
                                                            className="lower-avatar-thread"
                                                            css={
                                                                LowerAvatarThreadStyle
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </>
                                    )}
                                    {loadingGroupId !== group.id &&
                                        group.notifications
                                            .slice(1)
                                            .map((notification, index) => (
                                                <div
                                                    key={notification.id}
                                                    role="button"
                                                    tabIndex={0}
                                                    css={
                                                        nestedNotificationStyle
                                                    }
                                                    onMouseEnter={() =>
                                                        setHoveredId(
                                                            notification.id
                                                        )
                                                    }
                                                    onMouseLeave={() =>
                                                        setHoveredId(null)
                                                    }
                                                    onFocus={() =>
                                                        setHoveredId(
                                                            notification.id
                                                        )
                                                    }
                                                    onBlur={() =>
                                                        setHoveredId(null)
                                                    }
                                                    onClick={() => {
                                                        const url =
                                                            notification.content
                                                                .entity?.link
                                                                ?.url;
                                                        if (url) {
                                                            window.location.href =
                                                                url;
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (
                                                            e.key === "Enter" ||
                                                            e.key === " "
                                                        ) {
                                                            e.preventDefault();
                                                            const url =
                                                                notification
                                                                    .content
                                                                    .entity
                                                                    ?.link?.url;
                                                            if (url) {
                                                                window.location.href =
                                                                    url;
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <div
                                                        className="upper-avatar-thread"
                                                        css={
                                                            UpperAvatarThreadStyle
                                                        }
                                                    />
                                                    <div
                                                        css={
                                                            AvatarContainerStyle
                                                        }
                                                    >
                                                        <Avatar
                                                            size="large"
                                                            src={
                                                                notification
                                                                    .content
                                                                    .actors?.[0]
                                                                    ?.avatarUrl
                                                            }
                                                        />
                                                    </div>
                                                    <div
                                                        style={{
                                                            flex: 1
                                                        }}
                                                    >
                                                        <p
                                                            css={
                                                                firstNotificationStyle
                                                            }
                                                        >
                                                            {
                                                                notification
                                                                    .content
                                                                    .message
                                                            }
                                                            <span
                                                                css={
                                                                    timestampStyle
                                                                }
                                                            >
                                                                {" "}
                                                                {formatRelativeTime(
                                                                    notification.timestamp
                                                                )}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <ReadStateIndicator
                                                        isRead={
                                                            readStates[
                                                                notification.id
                                                            ] === "read"
                                                        }
                                                        isCardHovered={
                                                            notification.id ===
                                                            hoveredId
                                                        }
                                                        onToggle={async (e) => {
                                                            e.stopPropagation();
                                                            const notifIsRead =
                                                                readStates[
                                                                    notification
                                                                        .id
                                                                ] === "read";
                                                            const newState =
                                                                notifIsRead
                                                                    ? "unread"
                                                                    : "read";
                                                            setReadStates(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    [notification.id]:
                                                                        newState
                                                                })
                                                            );
                                                            await toggleReadState(
                                                                {
                                                                    notificationLogUrl,
                                                                    notificationId:
                                                                        notification.id,
                                                                    isCurrentlyRead:
                                                                        notifIsRead
                                                                }
                                                            );
                                                        }}
                                                    />
                                                    {index <
                                                        group.notifications
                                                            .length -
                                                            2 && (
                                                        <div
                                                            className="lower-avatar-thread"
                                                            css={
                                                                LowerAvatarThreadStyle
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                </>
                            )}

                            {/* Collapsed group for additional actors - nested inside the card */}
                            {hasMoreNotifications && (
                                <ExpandGroupButton
                                    additionalActors={group.additionalActors}
                                    notificationCount={group.size - 1}
                                    isExpanded={isExpanded}
                                    onToggle={() => {
                                        if (isExpanded) {
                                            setExpandedGroupId(null);
                                        } else {
                                            setExpandedGroupId(group.id);
                                            onExpand(
                                                group.id,
                                                group.notificationContinuationToken
                                            );
                                        }
                                    }}
                                />
                            )}
                        </div>
                    );
                }
            )}
        </div>
    );
};
export default NotificationGroup;
